// @flow
import * as yup from 'yup';
import moment from 'moment';

import * as trackingApi from 'Tracking/tracking-api';
import { getSiteSection } from 'Tracking/utils';
import { momentToApiDate } from 'Utils/date/apiDate';
import { graphqlifyString } from 'Utils/graphql';
import { WIDGET_QUOTATION_FORM_SUBMIT } from 'Utils/request/requestDictionary';
import graphQLClient from 'Utils/graphql-client/graphqlClient';

import {
  MIN_FORM_ADULTS,
  MIN_FORM_KIDS,
  MIN_FORM_TEENS,
  NO_VALUE,
} from './productQuotationForm.constants';

export type FormFieldOption = {
  name: string,
  value: string,
};

export type FormTraveler = {
  civility: string,
  email: string,
  firstName: string,
  lastName: string,
  phone: string,
};

type FormDepartureDate = string | { from: null | Date, to: null };

export type FormValues = {
  adultsNumber: string,
  comment: string,
  departureCity: string,
  departureDate: FormDepartureDate,
  extension?: string,
  kidsNumber: string,
  optinEmail: boolean,
  traveler: FormTraveler,
};

// Initialize form values

type InitialFormValuesParameters = {
  extensionOptions: $ReadOnlyArray<FormFieldOption>,
  isEscapade: boolean,
  isSummerCamp: boolean,
};

export const getInitialFormValues = ({
  extensionOptions,
  isEscapade,
  isSummerCamp,
}: InitialFormValuesParameters): FormValues => {
  return {
    adultsNumber: isSummerCamp ? MIN_FORM_TEENS.toString() : MIN_FORM_ADULTS.toString(),
    comment: '',
    departureCity: '',
    departureDate: isEscapade ? { from: null, to: null } : '',
    ...(extensionOptions.length > 0 ? { extension: '' } : null),
    // $FlowFixMe
    ...(isSummerCamp ? null : { kidsNumber: MIN_FORM_KIDS.toString() }),
    optinEmail: false,
    traveler: {
      civility: '',
      email: '',
      firstName: '',
      lastName: '',
      phone: '',
    },
  };
};

// Set form schema

type FormSchemaParameters = {
  civilityOptions: $ReadOnlyArray<FormFieldOption>,
  departureCityOptions: $ReadOnlyArray<FormFieldOption>,
  departureDateOptions: $ReadOnlyArray<FormFieldOption>,
  extensionOptions: $ReadOnlyArray<FormFieldOption>,
  isEscapade: boolean,
  isSummerCamp: boolean,
};

export const getFormSchema = (
  t: string => string,
  {
    civilityOptions,
    departureCityOptions,
    departureDateOptions,
    extensionOptions,
    isEscapade,
    isSummerCamp,
  }: FormSchemaParameters,
) => {
  const fistNameSchema = yup
    .string()
    .required(t('productQuotationForm.coordinates.firstName'))
    .max(20, t('productQuotationForm.coordinates.firstName'));
  const lastNameSchema = yup
    .string()
    .required(t('productQuotationForm.coordinates.lastName'))
    .max(30, t('productQuotationForm.coordinates.lastName'));

  return yup.object().shape({
    adultsNumber: yup.string().required(),
    comment: yup.string(),
    departureCity: yup
      .mixed()
      .oneOf(departureCityOptions.map(c => c.value))
      .required(),
    departureDate: isEscapade
      ? yup
          .object()
          .shape({
            from: yup.date(),
            to: yup.date().nullable(),
          })
          .required()
      : yup
          .mixed()
          .oneOf(departureDateOptions.map(d => d.value))
          .required(),
    ...(extensionOptions.length > 0
      ? {
          extension: yup
            .mixed()
            .oneOf(extensionOptions.map(e => e.value))
            .required(),
        }
      : null),
    // $FlowFixMe
    ...(isSummerCamp
      ? null
      : {
          kidsNumber: yup.string().required(),
        }),
    optinEmail: yup.boolean(),
    traveler: yup.object().shape({
      civility: yup
        .mixed()
        .oneOf(civilityOptions.map(c => c.value))
        .required(),
      email: yup
        .string()
        .email(t('productQuotationForm.coordinates.email'))
        .required(t('productQuotationForm.coordinates.email')),
      firstName: fistNameSchema,
      lastName: lastNameSchema,
      phone: yup
        .string()
        .required(t('productQuotationForm.coordinates.phoneNumber'))
        .matches(/^(\+[1-9]|0)?\d{1,14}$/, {
          excludeEmptyString: true,
          message: t('productQuotationForm.coordinates.phoneNumber'),
        }),
    }),
  });
};

// Submit form

export type FormStatusLink = {| label: string, url: string |};
export type FormStatus = {| link: FormStatusLink, text: string, title: string |};
export type FormProduct = {
  image: string,
  productId: string,
  productLabel: string,
  productRangeLabel: string,
  rangeId: string,
  seasonName: string,
  type: string,
};

export type TrackingData = {
  categoryGA: string,
  actionGA: string,
  labelGA: string,
  basePriceAccommodation: null | number,
  resortCode: string,
  resortDefaultPrice: null | number,
  resortNightNumber: number,
  resortSeasonType: string,
  siteSection: string,
};

type FormSubmitParameters = {
  actions: {
    setSubmitting: boolean => void,
    setStatus: ({ state: string }) => void,
  },
  appContext: AppContext,
  isSummerCamp: boolean,
  newsletter?: { id: string, description: string },
  product: FormProduct,
  quotation: { error: FormStatus, success: FormStatus },
  trackingData: TrackingData,
};

type Response = {
  productRequest: { statusCode: number },
};

export const FORM_STATUS = { NEW: 'new', SUCCESS: 'success', ERROR: 'error' };

const getFormattedDepartureDate = (date: FormDepartureDate): string => {
  return typeof date === 'string' ? date : momentToApiDate(moment(date.from || Date.now()));
};

export const getProductQuotationFormMutation = (values: FormValues, product: FormProduct) => {
  const {
    adultsNumber,
    kidsNumber = 0,
    comment,
    departureCity,
    departureDate,
    extension = '',
    optinEmail,
    traveler,
  } = values;
  const { productId, productLabel, productRangeLabel } = product;
  const { civility, email, firstName, lastName, phone } = traveler;
  const formattedDepartureDate = getFormattedDepartureDate(departureDate);
  return `mutation {
    productRequest(
      adultsCount: ${parseInt(adultsNumber, 10)}
      childrenCount: ${parseInt(kidsNumber, 10)}
      contact: {
        civility: "${civility}"
        firstName: "${firstName}"
        lastName: "${lastName}"
        email: "${email}"
        mobile: "${phone}"
      }
      customerComment: ${graphqlifyString(comment)}
      departureCity: "${departureCity || NO_VALUE}"
      departureDate: "${formattedDepartureDate}"
      extensionName: "${extension}"
      productId: "${productId}"
      productLabel: "${productLabel}"
      productRangeLabel: "${productRangeLabel}"
      optinEmail: ${optinEmail.toString()}
    ) {
      statusCode
    }
  }`;
};

export const getNewsletterSubscriptionMutation = (values: FormValues, newsletterId: string) => {
  const {
    traveler: { civility, email, firstName, lastName },
  } = values;
  return `mutation {
    subscription (
      civility: "${civility}"
      email: "${email}"
      firstName: "${firstName}"
      isSubscription: true
      lastName: "${lastName}"
      newsletterSubscriptions : ["${newsletterId}"]
      unicodeFirstName: "${firstName}"
      unicodeLastName: "${lastName}"
    ) {
      id
      neolaneId
    }
  }`;
};

export const handleSubmit = async (values: FormValues, params: FormSubmitParameters) => {
  const { actions, appContext, isSummerCamp, product, quotation, trackingData } = params;

  actions.setSubmitting(true);

  const productQuotationFormMutation = getProductQuotationFormMutation(values, product);

  try {
    // Post request
    const { productRequest }: Response = await graphQLClient.runNoCacheQuery(
      productQuotationFormMutation,
      appContext,
      {
        requestName: WIDGET_QUOTATION_FORM_SUBMIT,
      },
    );

    if (productRequest.statusCode === 500) {
      actions.setStatus({ state: FORM_STATUS.ERROR, ...quotation.error });
    } else {
      if (isSummerCamp) {
        const pageType = 'landing';
        trackingApi.setDataLayer(
          {
            actionGA: 'SummerCamp',
            categoryGA: pageType,
            labelGA: 'CTASendQuote',
            siteSection: getSiteSection({ pageType }),
          },
          appContext,
        );
      }
      actions.setStatus({ state: FORM_STATUS.SUCCESS, ...quotation.success });
    }

    if (!isSummerCamp) {
      const adultsNumber = parseInt(values.adultsNumber, 10);
      const kidsNumber = parseInt(values.kidsNumber, 10);
      const hasNoTransport = values.departureCity === '' || values.departureCity === 'NO';
      const formattedDepartureDate = getFormattedDepartureDate(values.departureDate);
      trackingApi.setDataLayer(
        {
          ...trackingData,
          resortAdult: adultsNumber,
          resortChildren: kidsNumber,
          resortDepartureCityCode: hasNoTransport ? 'without transport' : values.departureCity,
          resortDepartureDate: formattedDepartureDate,
          resortTotalParticipant: adultsNumber + kidsNumber,
          resortTransport: hasNoTransport ? 'without transport' : 'with transport',
        },
        appContext,
      );
    }
  } catch (error) {
    actions.setStatus({ state: FORM_STATUS.ERROR, ...quotation.error });
  } finally {
    actions.setSubmitting(false);
  }
};
